/**
 * How a subscription's price and billing period are written, in ONE place.
 *
 * The manage modal and the profile card each grew their own copy of this. When
 * the modal learned that a lifetime plan is billed once rather than monthly,
 * the profile card did not, and a $200 lifetime purchase still read
 * "$200.00/mo (Monthly)" on the surface most people actually look at
 * (Codex #830). Two formatters describing one thing is the drift; a shared one
 * is the fix.
 *
 * The month unit differs by surface on purpose: the compact profile card says
 * "/mo", the roomier modal says "/month". That is the only difference, and it
 * is a parameter rather than a second function.
 */

/** The backend sends 'annual' where the schema says 'yearly'. Both mean a year. */
const YEARLY = ['yearly', 'annual'];
const MONTHLY = ['monthly', 'month'];

export const isLifetimePeriod = (period?: string | null): boolean => period === 'lifetime';

export function formatSubscriptionPrice(
  price: string | number | undefined | null,
  period: string | undefined | null,
  options: { monthUnit?: 'mo' | 'month' } = {},
): string {
  if (!price) return '';

  const priceNum = parseFloat(String(price));
  if (!Number.isFinite(priceNum)) return '';

  const amount = `$${priceNum.toFixed(2)}`;

  // Not billed per period at all, so no "/" suffix is truthful. The old
  // else-branch called anything that was not yearly "monthly" — including the
  // schema's own 'lifetime' value.
  if (isLifetimePeriod(period)) return `${amount} once`;
  if (YEARLY.includes(period ?? '')) return `${amount}/year`;
  if (MONTHLY.includes(period ?? '')) return `${amount}/${options.monthUnit ?? 'month'}`;

  // Unknown or absent: state the PRICE and claim no cadence. Falling through to
  // a monthly unit was the same guess billingPeriodLabel() already refuses to
  // make, so "quarterly" still rendered "$30.00/mo" while the parenthetical
  // beside it correctly said nothing — half-fixed, and the wrong half visible
  // (Codex #830).
  return amount;
}

/**
 * The parenthetical next to the price. Returns null for a period we do not
 * recognise, because labelling an unknown value "Monthly" is how a lifetime
 * purchase came to be described as a monthly one.
 */
export function billingPeriodLabel(period?: string | null): string | null {
  if (isLifetimePeriod(period)) return 'One-time';
  if (YEARLY.includes(period ?? '')) return 'Annual';
  if (MONTHLY.includes(period ?? '')) return 'Monthly';

  return null;
}
